/**
 * Dil katmanı — anahtarı İNGİLİZCE METNİN KENDİSİ olan sözlük.
 *
 * NEDEN ANAHTAR = METİN: `mrb.detay.baslik` gibi sembolik anahtarlarda sözlükte karşılık
 * bulunamazsa ekrana ham anahtar düşer. Burada karşılık bulunamazsa İngilizce metin düşer,
 * yani yarım çeviri en kötü ihtimalle "bu satır henüz çevrilmemiş" demektir, bozuk ekran değil.
 * Kaynak kodu da okunur kalır: `t("Field list")` neyi yazdığını söyler.
 *
 * NEDEN DİL DEĞİŞİNCE SAYFA YENİLENİYOR: kodda modül düzeyinde hesaplanan sabitler var
 * (Saha.tsx'teki `BASLIK`, Kabuk.tsx'teki `NAV` gibi). Bunlar import anında BİR KEZ çalışır;
 * React bağlamıyla dil değiştirilirse o sabitler eski dilde kalır ve ekranda sessizce karışık
 * dil oluşur. Yenileme bu sınıf hatanın tamamını imkânsız kılar; dil değişimi yılda birkaç kez
 * yapılan bir iştir, maliyeti yok.
 *
 * Sözlük statik import: iç uygulama, ~30 KB gzip fark eder. Dinamik import bu dosyanın
 * modül düzeyinde çalışan `t()` çağrılarından ÖNCE tamamlanmak zorunda olurdu, kırılgan.
 */
import { TR } from "./sozluk/tr";

export type Dil = "en" | "tr";

const KEY = "oasis.dil";
export const DILLER: { kod: Dil; ad: string; kisa: string }[] = [
  { kod: "en", ad: "English", kisa: "EN" },
  { kod: "tr", ad: "Türkçe", kisa: "TR" },
];

/** İngilizce kaynak dildir: sözlüğü yok, metin olduğu gibi geçer. */
const SOZLUKLER: Record<Dil, Record<string, string>> = { en: {}, tr: TR };

function oku(): Dil {
  try {
    return localStorage.getItem(KEY) === "tr" ? "tr" : "en";
  } catch {
    return "en";
  }
}

const aktifDil = oku();
const sozluk = SOZLUKLER[aktifDil];

if (typeof document !== "undefined") document.documentElement.lang = aktifDil;

export const dil = (): Dil => aktifDil;

/**
 * Tarih/sayı biçimi için BCP-47 etiketi. İngilizce için bilinçli olarak `en-GB`, `en-US` değil:
 * `en-US` ayı önce yazar (9/6/2026) ve izlenebilirlik kaydında gün/ay karışması kabul edilemez.
 * `en-GB` ve `tr-TR` ikisi de gün-ay-yıl sırasında, dil değişimi tarihin anlamını değiştirmez.
 */
export const bicimDili = (): string => (aktifDil === "tr" ? "tr-TR" : "en-GB");

/** `{0}`, `{1}` … yer tutucularını doldurur. Argüman yoksa metne hiç dokunulmaz. */
function doldur(kalip: string, arg: readonly unknown[]): string {
  if (!arg.length) return kalip;
  return kalip.replace(/\{(\d+)\}/g, (t, n) => {
    const d = arg[Number(n)];
    return d === undefined ? t : String(d);
  });
}

/**
 * Etiketli şablonun anahtarı: pişmiş parçalar `{0}`, `{1}` … ile birleştirilir.
 * ``cevir`${ad} okundu` `` → anahtar `"{0} okundu"`.
 * Geçersiz kaçış dizisi varsa pişmiş parça `undefined` olur, ham parçaya düşülür.
 */
function sablonAnahtari(parcalar: TemplateStringsArray): string {
  let k = "";
  for (let i = 0; i < parcalar.length; i++) {
    if (i) k += `{${i - 1}}`;
    k += parcalar[i] ?? parcalar.raw[i] ?? "";
  }
  return k;
}

/**
 * KALIP EŞLEME — sunucudan gelen, ara değer taşıyan cümleler için.
 *
 * Sunucu istemciye birleşmiş cümle gönderiyor ("12 risers received"); sözlükte bunun sabit
 * karşılığı yok, çünkü sayı her seferinde başka. Sözlükte kalıp var (`{0} risers received`),
 * o yüzden doğrudan karşılık bulunamayınca kalıplara bakılır, ara değerler yakalanıp Türkçe
 * kalıba yerleştirilir.
 *
 * Belirsizliğe karşı iki kural: kalıplar SABİT METNİ EN UZUN olandan başlayarak denenir ve
 * altı karakterden az sabit metni olan kalıp hiç denenmez — yoksa `{0}` gibi bir kalıp her
 * şeye uyar. Sonuç önbelleğe alınır; her benzersiz metin için tarama bir kez yapılır.
 */
const KALIP_EN_AZ_SABIT = 6;
/**
 * Yakalanan değeri ", " ile ayrılmış parça listesi olan kalıplar; her parça kendi kalıbıyla çevrilir ("remaining {0} → {1} {2}",
 * "PO {0} → {1}", "note"). Örn. "Stock package updated: PKG-0023 (PO — → 2026-0588, remaining 60 → 55 m)". Yalnız bu kalıplarda
 * parçalar tek tek sözlükten ya da kalıptan geçer; öteki kalıplarda yakalanan değer (not, ad, kod) bölünmez, serbest metin bozulmaz.
 */
const PARCALI_KALIPLAR = new Set(["Stock package updated: {0} ({1})"]);
let kaliplar: { desen: RegExp; karsilik: string; parcali: boolean }[] | null = null;
const kacir = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function kaliplariKur() {
  if (kaliplar) return kaliplar;
  const liste: { desen: RegExp; karsilik: string; parcali: boolean; sabit: number }[] = [];
  for (const [anahtar, karsilik] of Object.entries(sozluk)) {
    if (!/\{\d+\}/.test(anahtar)) continue;
    const sabit = anahtar.replace(/\{\d+\}/g, "").length;
    if (sabit < KALIP_EN_AZ_SABIT) continue;
    const govde = anahtar.split(/\{\d+\}/).map(kacir).join("(.*?)");
    liste.push({ desen: new RegExp(`^${govde}$`, "s"), karsilik, parcali: PARCALI_KALIPLAR.has(anahtar), sabit });
  }
  liste.sort((a, b) => b.sabit - a.sabit);
  kaliplar = liste.map(({ desen, karsilik, parcali }) => ({ desen, karsilik, parcali }));
  return kaliplar;
}

const kalipOnbellek = new Map<string, string | null>();

function kalipla(metin: string): string | null {
  const onbellekte = kalipOnbellek.get(metin);
  if (onbellekte !== undefined) return onbellekte;
  let sonuc: string | null = null;
  for (const k of kaliplariKur()) {
    const m = k.desen.exec(metin);
    if (!m) continue;
    // Yakalanan değerin kendisi de çevrilebilir metin olabilir ("active cycle · all batches").
    // Sözlükte doğrudan karşılığı varsa kullanılır; yoksa değer olduğu gibi kalır — sayı, RN
    // kodu ve tarih bu yolla asla bozulmaz. Parçalı kalıpta (PARCALI_KALIPLAR) parçalar tek tek.
    sonuc = k.karsilik.replace(/\{(\d+)\}/g, (t, n) => {
      const yakalanan = m[Number(n) + 1];
      if (yakalanan === undefined) return t;
      const dogrudan = sozluk[yakalanan];
      if (dogrudan !== undefined || !k.parcali) return dogrudan ?? yakalanan;
      return yakalanan
        .split(", ")
        .map((p) => sozluk[p] ?? kalipla(p) ?? p)
        .join(", ");
    });
    break;
  }
  kalipOnbellek.set(metin, sonuc);
  return sonuc;
}

/**
 * Çeviri. Üç biçimde çağrılır:
 *   cevir("Field list")                       düz metin
 *   cevir("{0} of {1} read", okunan, toplam)  yer tutuculu metin
 *   cevir`${riser.rn} verification read`      etiketli şablon (yapı bozulmadan sarılır)
 *
 * ADI NEDEN `t` DEĞİL: kaynakta `t` birçok dosyada yerel değişken olarak bağlı (map
 * parametresi, ayrıştırma sonucu). İçe aktarılan `t` bu kapsamlarda sessizce gölgelenir;
 * yerel `t` de bir fonksiyonsa tip denetimi bile fark etmez. `cevir` kaynakta hiç geçmiyor.
 */
export function cevir(metin: string, ...arg: unknown[]): string;
export function cevir(parcalar: TemplateStringsArray, ...arg: unknown[]): string;
export function cevir(girdi: string | TemplateStringsArray, ...arg: unknown[]): string {
  const anahtar = typeof girdi === "string" ? girdi : sablonAnahtari(girdi);
  const dogrudan = sozluk[anahtar];
  if (dogrudan !== undefined) return doldur(dogrudan, arg);
  // Ara değer taşımayan düz metinde kalıp eşlemeyi dene; hiçbiri tutmazsa metnin kendisi.
  if (typeof girdi === "string" && !arg.length && !/\{\d+\}/.test(anahtar)) {
    const kalipli = kalipla(anahtar);
    if (kalipli !== null) return kalipli;
  }
  return doldur(anahtar, arg);
}

/** Dili değiştirir ve sayfayı yeniler (yukarıdaki gerekçe). */
export function dilAyarla(d: Dil): void {
  if (d === aktifDil) return;
  try {
    localStorage.setItem(KEY, d);
  } catch {}
  location.reload();
}
